import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { useOrganization } from '../context/OrganizationContext';
import { useMode } from '../context/ModeContext';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { SignalRow, SignalDetail, SimulatePanel, AttentionHeader, BatchApprovePrompt } from '../components/SignalComponents';
import { useUsageStatus, CreditWarningBanner, BlockedModal } from '../components/UsageEnforcement';
import { TopUpModal } from '../components/TopUpModal';
import {
  Lightning,
  CheckCircle,
  XCircle,
  Clock,
  PaperPlaneTilt,
  CalendarPlus,
  PhoneCall,
  Trash,
  CircleNotch,
  ArrowClockwise,
  WarningCircle,
  ListChecks,
  Robot,
  ArrowRight,
  Plus,
  EnvelopeSimple,
  PencilSimple,
  ShieldCheck,
  Broadcast,
} from '@phosphor-icons/react';

/* ─── Helpers ─── */
const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

/* ─── Trigger source badge config ─── */
const TRIGGER_SOURCE_CONFIG = {
  email:            { label: 'Email',        color: '#818CF8', bg: 'rgba(99,102,241,0.08)' },
  email_inbound:    { label: 'Email',        color: '#818CF8', bg: 'rgba(99,102,241,0.08)' },
  form_inbound:     { label: 'Website Form', color: '#34D399', bg: 'rgba(52,211,153,0.08)' },
  webhook:          { label: 'Webhook',      color: '#F97316', bg: 'rgba(249,115,22,0.08)' },
  webhook_inbound:  { label: 'Webhook',      color: '#F97316', bg: 'rgba(249,115,22,0.08)' },
  sms_inbound:      { label: 'SMS',          color: '#06B6D4', bg: 'rgba(6,182,212,0.08)' },
  sms:              { label: 'SMS',          color: '#06B6D4', bg: 'rgba(6,182,212,0.08)' },
  scheduled:        { label: 'Schedule',     color: '#A78BFA', bg: 'rgba(167,139,250,0.08)' },
  schedule:         { label: 'Schedule',     color: '#A78BFA', bg: 'rgba(167,139,250,0.08)' },
  system_run:       { label: 'System',       color: '#3B82F6', bg: 'rgba(59,130,246,0.08)' },
  calendar:         { label: 'Calendar',     color: '#FB923C', bg: 'rgba(251,146,60,0.08)' },
  manual:           { label: 'Manual',       color: '#7D8896', bg: 'rgba(107,118,133,0.08)' },
  manual_test:      { label: 'Test',         color: '#7D8896', bg: 'rgba(107,118,133,0.08)' },
  test_simulation:  { label: 'Test',         color: '#7D8896', bg: 'rgba(107,118,133,0.08)' },
  command:          { label: 'Command',      color: '#00E5C0', bg: 'rgba(0,229,192,0.08)' },
  voice:            { label: 'Voice',        color: '#EC4899', bg: 'rgba(236,72,153,0.08)' },
};

const TriggerBadge = ({ source }) => {
  const cfg = TRIGGER_SOURCE_CONFIG[source];
  if (!cfg) return null;
  return (
    <span
      data-testid={`trigger-badge-${source}`}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium"
      style={{ background: cfg.bg, color: cfg.color }}
    >
      {cfg.label}
    </span>
  );
};

const CreditsBadge = ({ credits }) => {
  if (credits == null || credits === undefined) return null;
  return (
    <span
      data-testid="credits-used-badge"
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-[rgba(255,255,255,0.04)] text-[#7D8896]"
    >
      {Number(credits).toFixed(1)} cr
    </span>
  );
};

const STATUS_BADGE = {
  completed:      { label: 'Completed',  color: '#00E5C0', bg: 'rgba(0,229,192,0.08)', icon: CheckCircle },
  failed:         { label: 'Failed',     color: '#FF6B6B', bg: 'rgba(255,107,107,0.08)', icon: XCircle },
  pending_review: { label: 'Review',     color: '#F59E0B', bg: 'rgba(245,158,11,0.08)', icon: Clock },
  needs_action:   { label: 'Action',     color: '#F59E0B', bg: 'rgba(245,158,11,0.08)', icon: WarningCircle },
  approved:       { label: 'Approved',   color: '#00E5C0', bg: 'rgba(0,229,192,0.08)', icon: CheckCircle },
  dismissed:      { label: 'Dismissed',  color: '#7D8896', bg: 'rgba(107,118,133,0.08)', icon: XCircle },
  closed:         { label: 'Done',       color: '#7D8896', bg: 'rgba(107,118,133,0.08)', icon: CheckCircle },
};

/* Extract sender from execution data */
const _extractSender = (run) => {
  const input = run.input || {};
  const enquiry = input.enquiry || '';
  const match = enquiry.match(/Email from (.+?)[\n\r]/);
  if (match) return match[1];
  if (run.trigger_data?.from) return run.trigger_data.from;
  return input.customer_name || 'Unknown';
};

/* Extract subject from execution data */
const _extractSubject = (run) => {
  const input = run.input || {};
  const enquiry = input.enquiry || '';
  const match = enquiry.match(/Subject: (.+?)[\n\r]/);
  if (match) return match[1];
  if (run.trigger_data?.subject) return run.trigger_data.subject;
  return null;
};

/* ═══════════════════════════════════════════════════
   ACTIVITY ROW — business-friendly proof feed
   ═══════════════════════════════════════════════════ */
const ActivityRow = ({ run, isActive, onSelect }) => {
  const badge = STATUS_BADGE[run.status] || STATUS_BADGE.completed;
  const BadgeIcon = badge.icon;
  const isEmail = run.trigger_source === 'email' || run.trigger_source === 'email_inbound';
  const sender = _extractSender(run);
  const subject = _extractSubject(run);
  const headline = isEmail
    ? `Email from ${sender}`
    : run.summary || run.automation_name || 'Worker activity';

  return (
    <button
      onClick={() => onSelect(run)}
      data-testid={`run-${run.id}`}
      className={`w-full text-left px-4 py-3.5 border-b border-[rgba(255,255,255,0.04)] transition-colors ${
        isActive ? 'bg-[rgba(0,229,192,0.04)]' : 'hover:bg-[rgba(255,255,255,0.02)]'
      }`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: badge.bg, color: badge.color }}>
            <BadgeIcon size={10} weight="fill" />
            {badge.label}
          </span>
          <TriggerBadge source={run.trigger_source} />
          <CreditsBadge credits={run.credits_used} />
        </div>
        <span className="text-[10px] text-[#7D8896]">{timeAgo(run.created_at)}</span>
      </div>
      <p className="text-sm text-[#F0F4F8] truncate">{headline}</p>
      {isEmail && subject && (
        <p className="text-[11px] text-[#7D8896] mt-0.5 truncate">Re: {subject}</p>
      )}
      <p className="text-[11px] text-[#7D8896] mt-0.5 truncate">
        {run.automation_name ? `${run.automation_name} handled this` : ''}
      </p>
    </button>
  );
};

/* ═══════════════════════════════════════════════════
   EXECUTION STEP — trust-aware timeline entry
   ═══════════════════════════════════════════════════ */
const STEP_MODE_STYLES = {
  trigger:  { icon: PaperPlaneTilt, color: '#818CF8', border: 'rgba(99,102,241,0.15)', label: 'Received' },
  ai:       { icon: Robot,          color: '#A5A8FC', border: 'rgba(139,143,245,0.15)', label: 'AI' },
  auto:     { icon: CheckCircle,    color: '#00E5C0', border: 'rgba(0,229,192,0.15)',   label: 'Executed' },
  fallback: { icon: Clock,          color: '#F59E0B', border: 'rgba(245,158,11,0.15)',  label: 'Draft' },
  logic:    { icon: Lightning,      color: '#7D8896', border: 'rgba(255,255,255,0.06)', label: 'Logic' },
  error:    { icon: XCircle,        color: '#FF6B6B', border: 'rgba(255,107,107,0.15)', label: 'Failed' },
};

const ExecutionStep = ({ step, index, isLast }) => {
  const [expanded, setExpanded] = useState(false);
  const mode = step.execution_mode || 'auto';
  const style = STEP_MODE_STYLES[mode] || STEP_MODE_STYLES.auto;
  const StepIcon = style.icon;
  const trustLabel = step.trust_label || style.label;

  const stepTime = step.timestamp
    ? new Date(step.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : null;

  return (
    <div data-testid={`execution-step-${index}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left bg-[#1E2836] border rounded-lg px-4 py-3 transition-colors hover:bg-[rgba(255,255,255,0.02)]"
        style={{ borderColor: style.border }}
      >
        <div className="flex items-center gap-2 mb-1">
          <StepIcon size={12} style={{ color: style.color }} weight="fill" />
          <span className="text-xs font-medium text-[#F0F4F8] flex-1">{step.step_label}</span>
          {stepTime && <span className="text-[9px] text-[#5C6778]">{stepTime}</span>}
        </div>
        {/* Trust indicator */}
        <div className="flex items-center gap-2 mt-1.5">
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold"
            style={{ color: style.color, backgroundColor: `${style.color}12` }}
            data-testid={`trust-label-${index}`}>
            {trustLabel}
          </span>
          {step.tool_used && (
            <span className="text-[9px] text-[#7D8896]">via {step.tool_used === 'gmail' ? 'Gmail' : step.tool_used === 'outlook' ? 'Outlook' : step.tool_used === 'google_calendar' ? 'Google Calendar' : step.tool_used}</span>
          )}
        </div>

        {/* Expandable output */}
        {expanded && step.output && (
          <div className="mt-2.5 pt-2 border-t border-[rgba(255,255,255,0.04)]">
            <p className="text-xs text-[#7D8896] whitespace-pre-wrap">{step.output}</p>
          </div>
        )}
      </button>
      {!isLast && (
        <div className="flex justify-center"><div className="w-px h-3 bg-[rgba(255,255,255,0.06)]" /></div>
      )}
    </div>
  );
};


const ActivityDetail = ({ run }) => {
  if (!run) return null;
  const badge = STATUS_BADGE[run.status] || STATUS_BADGE.completed;
  const BadgeIcon = badge.icon;
  const isEmail = run.trigger_source === 'email' || run.trigger_source === 'email_inbound';
  const sender = _extractSender(run);
  const subject = _extractSubject(run);
  const triggerCfg = TRIGGER_SOURCE_CONFIG[run.trigger_source];

  return (
    <div className="p-6 space-y-6" data-testid="run-detail">
      {/* What happened — business-friendly headline */}
      <div className="bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-xl p-5">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
            isEmail ? 'bg-[rgba(99,102,241,0.1)]' : 'bg-[rgba(0,229,192,0.1)]'
          }`}>
            {isEmail
              ? <PaperPlaneTilt size={18} className="text-[#818CF8]" weight="duotone" />
              : <Lightning size={18} className="text-[#00E5C0]" weight="duotone" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-base font-semibold text-[#F0F4F8]">
              {isEmail ? `Email from ${sender}` : run.summary || 'Worker ran'}
            </p>
            {isEmail && subject && (
              <p className="text-sm text-[#B0B8C4] mt-0.5">Subject: {subject}</p>
            )}
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: badge.bg, color: badge.color }}>
                <BadgeIcon size={10} weight="fill" />
                {badge.label}
              </span>
              <TriggerBadge source={run.trigger_source} />
              <CreditsBadge credits={run.credits_used} />
              <span className="text-[10px] text-[#7D8896]">{timeAgo(run.created_at)}</span>
              {run.duration_ms && (
                <span className="text-[10px] text-[#5C6778]">
                  {run.duration_ms < 1000 ? `${run.duration_ms}ms` : `${(run.duration_ms / 1000).toFixed(1)}s`}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Timeline — what your business did automatically */}
      <div>
        <h3 className="text-xs font-semibold text-[rgba(255,255,255,0.75)] uppercase tracking-[0.08em] pb-2 border-b border-[rgba(255,255,255,0.08)] mb-3">What happened</h3>
        <div className="space-y-0">
          {/* Email received step (if email trigger) */}
          {isEmail && !(run.execution_log || []).some(s => s.execution_mode === 'trigger') && (
            <div data-testid="timeline-email-received">
              <div className="bg-[#1E2836] border border-[rgba(99,102,241,0.15)] rounded-lg px-4 py-3">
                <div className="flex items-center gap-2 mb-1">
                  <PaperPlaneTilt size={12} className="text-[#818CF8]" weight="fill" />
                  <span className="text-xs font-medium text-[#818CF8]">Email received</span>
                </div>
                <p className="text-sm text-[#F0F4F8]">
                  From {sender}{subject ? ` — "${subject}"` : ''}
                </p>
              </div>
              <div className="flex justify-center"><div className="w-px h-3 bg-[rgba(255,255,255,0.06)]" /></div>
            </div>
          )}

          {/* Step-by-step execution log with trust indicators */}
          {(run.execution_log || []).map((step, i) => (
            <ExecutionStep key={i} step={step} index={i} isLast={i === (run.execution_log || []).length - 1} />
          ))}

          {/* Fallback for legacy runs without execution_log */}
          {(!run.execution_log || run.execution_log.length === 0) && (
            <>
              <div data-testid="timeline-worker-triggered">
                <div className="bg-[#1E2836] border border-[rgba(0,229,192,0.15)] rounded-lg px-4 py-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Robot size={12} className="text-[#00E5C0]" weight="fill" />
                    <span className="text-xs font-medium text-[#00E5C0]">Worker triggered</span>
                  </div>
                  <p className="text-sm text-[#F0F4F8]">{run.automation_name || 'AI Worker'}</p>
                </div>
                <div className="flex justify-center"><div className="w-px h-3 bg-[rgba(255,255,255,0.06)]" /></div>
              </div>
              <div>
                <div className="bg-[#1E2836] border border-[rgba(0,229,192,0.15)] rounded-lg px-4 py-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Robot size={12} className="text-[#00E5C0]" weight="fill" />
                    <span className="text-xs font-medium text-[#00E5C0]">AI generated response</span>
                  </div>
                  <p className="text-xs text-[#7D8896]">Reply prepared for review</p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Output — the result */}
      {run.final_output && (
        <div>
          <h3 className="text-xs font-semibold text-[#7D8896] uppercase tracking-[0.08em] mb-3">Output</h3>
          <div className="bg-[#243041] border border-[rgba(0,229,192,0.1)] rounded-xl p-4">
            <p className="text-sm text-[#F0F4F8] whitespace-pre-wrap">{run.final_output}</p>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   WORK ITEM — Action queue
   ═══════════════════════════════════════════════════ */
const workItemLabel = (item) => {
  if (item.reply) return 'Review draft reply';
  if (item.enquiry) return 'Review customer enquiry';
  return 'Review and decide';
};

const WorkItem = ({ item, isActive, onSelect }) => {
  const needsAction = item.status === 'pending_review' || item.status === 'needs_action';
  return (
    <button
      onClick={() => onSelect(item)}
      data-testid={`work-item-${item.id}`}
      className={`w-full text-left px-4 py-3.5 border-b border-[rgba(255,255,255,0.04)] transition-colors ${
        isActive ? 'bg-[rgba(245,158,11,0.04)]' : 'hover:bg-[rgba(255,255,255,0.02)]'
      }`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
          needsAction ? 'bg-[rgba(245,158,11,0.1)] text-[#F59E0B]' : 'bg-[rgba(107,118,133,0.1)] text-[#7D8896]'
        }`}>
          {needsAction ? <WarningCircle size={10} weight="fill" /> : <CheckCircle size={10} weight="fill" />}
          {needsAction ? 'Needs attention' : item.status === 'approved' ? 'Approved' : item.status === 'dismissed' ? 'Dismissed' : 'Done'}
        </span>
        <span className="text-[10px] text-[#7D8896]">{timeAgo(item.created_at)}</span>
      </div>
      <p className="text-sm text-[#F0F4F8]">{workItemLabel(item)}</p>
      <p className="text-[11px] text-[#7D8896] mt-0.5 truncate">
        {item.customer_name ? `${item.customer_name}` : ''}{item.automation_name ? ` ${'\u00b7'} via ${item.automation_name}` : ''}
      </p>
    </button>
  );
};

const WorkDetail = ({ item, onAction, acting }) => {
  if (!item) return null;
  const needsAction = item.status === 'pending_review' || item.status === 'needs_action';
  const [isEditing, setIsEditing] = useState(false);
  const [editedReply, setEditedReply] = useState(item.reply || '');

  // Parse email metadata
  const customerEmail = item.customer_email || (() => {
    const match = item.enquiry?.match(/Email from (.+?)(\n|$)/);
    return match ? match[1].trim() : '';
  })();
  const subject = item.subject || (() => {
    const match = item.enquiry?.match(/Subject: (.+?)(\n|$)/);
    return match ? `Re: ${match[1].trim()}` : 'Regarding your enquiry';
  })();
  const sendingVia = (() => {
    const gmail = item.tool_statuses?.find(t => t.tool === 'email' && t.connected);
    return gmail ? 'Gmail' : 'Email';
  })();

  return (
    <div className="p-6 space-y-5" data-testid="work-detail">
      {/* ── What happened (original enquiry) ── */}
      {item.enquiry && (
        <div>
          <h3 className="text-xs font-semibold text-[#7D8896] uppercase tracking-[0.08em] mb-2.5">Enquiry received</h3>
          <div className="bg-[#243041] border border-[rgba(255,255,255,0.08)] rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] text-[#7D8896] uppercase tracking-[0.08em]">From</span>
              <span className="text-xs text-[#F0F4F8] font-medium">{customerEmail || item.customer_name || 'Customer'}</span>
              <span className="text-[10px] text-[#7D8896] ml-auto">{timeAgo(item.created_at)}</span>
            </div>
            <p className="text-sm text-[#B0B8C4]">{item.enquiry}</p>
          </div>
        </div>
      )}

      {/* ── Draft reply — structured email view ── */}
      {item.reply && (
        <div>
          <h3 className="text-xs font-semibold text-[#7D8896] uppercase tracking-[0.08em] mb-2.5">
            <span className="flex items-center gap-1.5">
              <ShieldCheck size={12} className="text-[#00E5C0]" weight="fill" />
              Draft reply ready to send
            </span>
          </h3>
          <div className="bg-[#243041] border border-[rgba(0,229,192,0.12)] rounded-xl overflow-hidden" data-testid="email-preview">
            {/* Email header fields */}
            <div className="px-4 py-3 space-y-1.5 border-b border-[rgba(255,255,255,0.04)] bg-[rgba(255,255,255,0.01)]">
              <div className="flex items-center gap-2 text-xs">
                <span className="text-[#7D8896] w-14 shrink-0">From</span>
                <span className="text-[#F0F4F8]">{item.automation_name || 'Your business'}</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-[#7D8896] w-14 shrink-0">To</span>
                <span className="text-[#F0F4F8]">{customerEmail || item.customer_name || '—'}</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-[#7D8896] w-14 shrink-0">Subject</span>
                <span className="text-[#F0F4F8] font-medium">{subject}</span>
              </div>
            </div>

            {/* Email body */}
            <div className="px-4 py-4">
              {isEditing ? (
                <textarea
                  value={editedReply}
                  onChange={(e) => setEditedReply(e.target.value)}
                  rows={8}
                  className="w-full bg-[rgba(255,255,255,0.03)] border border-[rgba(0,229,192,0.15)] rounded-lg px-3 py-2.5 text-sm text-[#F0F4F8] focus:outline-none focus:ring-1 focus:ring-[rgba(0,229,192,0.3)] resize-y"
                  data-testid="edit-reply-textarea"
                  autoFocus
                />
              ) : (
                <p className="text-sm text-[#F0F4F8] whitespace-pre-wrap leading-relaxed" data-testid="reply-body">
                  {editedReply || item.reply}
                </p>
              )}
            </div>

            {/* Sending via footer */}
            <div className="px-4 py-2 border-t border-[rgba(255,255,255,0.04)] bg-[rgba(255,255,255,0.01)]">
              <div className="flex items-center gap-1.5">
                <EnvelopeSimple size={11} className="text-[#7D8896]" />
                <span className="text-[10px] text-[#7D8896]">Sending via {sendingVia}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Actions ── */}
      {needsAction && (
        <div className="border-t border-[rgba(255,255,255,0.08)] pt-5">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => {
                const payload = editedReply !== item.reply ? { edited_reply: editedReply } : {};
                onAction(item.id, 'approve_send', payload);
              }}
              disabled={acting}
              data-testid="action-approve-send"
              className="flex items-center gap-2 px-5 py-2.5 bg-[#00E5C0] text-[#12161C] text-sm font-semibold rounded-lg hover:bg-[#00CCB0] transition-colors disabled:opacity-50"
            >
              {acting ? <CircleNotch size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} weight="bold" />}
              Approve & Send
            </button>
            <button
              onClick={() => setIsEditing(!isEditing)}
              data-testid="action-edit-reply"
              className="flex items-center gap-2 px-4 py-2.5 text-sm text-[#F0F4F8] border border-[rgba(255,255,255,0.08)] rounded-lg hover:bg-[rgba(255,255,255,0.04)] transition-colors font-medium"
            >
              <PencilSimple size={14} />
              {isEditing ? 'Done editing' : 'Edit before sending'}
            </button>
            <button
              onClick={() => onAction(item.id, 'dismiss')}
              disabled={acting}
              data-testid="action-dismiss"
              className="flex items-center gap-2 px-3 py-2.5 text-sm text-[#7D8896] border border-[rgba(255,255,255,0.08)] rounded-lg hover:bg-[rgba(255,255,255,0.04)] hover:text-[#B0B8C4] transition-colors disabled:opacity-50"
            >
              <Trash size={14} />
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Already actioned */}
      {!needsAction && item.actioned_by && (
        <div className="bg-[rgba(0,229,192,0.04)] border border-[rgba(0,229,192,0.1)] rounded-xl p-4 flex items-center gap-2">
          <CheckCircle size={16} className="text-[#00E5C0]" weight="fill" />
          <span className="text-sm text-[#B0B8C4]">
            {item.status === 'approved' ? 'Approved & sent' : item.status === 'dismissed' ? 'Dismissed' : 'Done'} by {item.actioned_by}
          </span>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   MAIN — WORK + ACTIVITY
   ═══════════════════════════════════════════════════ */
export default function ServiceInbox() {
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  const { mode } = useMode();
  const isOperate = mode === 'operate';
  const [tab, setTab] = useState('signals');
  const [runs, setRuns] = useState([]);
  const [workItems, setWorkItems] = useState([]);
  const [signals, setSignals] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [selectedWork, setSelectedWork] = useState(null);
  const [selectedSignal, setSelectedSignal] = useState(null);
  const [startSignalEdit, setStartSignalEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [approvingId, setApprovingId] = useState(null);
  const [blockedModal, setBlockedModal] = useState(null);
  const [showTopUp, setShowTopUp] = useState(false);

  const orgId = activeOrganization?.organization_id;
  const { creditStatus, voiceStatus, refresh: refreshUsage } = useUsageStatus();

  const fetchData = async () => {
    if (!orgId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [runsRes, workRes, signalsRes, scenariosRes] = await Promise.all([
        fetchWithOrg(`${BACKEND_URL}/api/service-os/runs`, {}, orgId),
        fetchWithOrg(`${BACKEND_URL}/api/service-os/work`, {}, orgId),
        fetchWithOrg(`${BACKEND_URL}/api/signals`, {}, orgId),
        fetchWithOrg(`${BACKEND_URL}/api/signals/scenarios/list`, {}, orgId),
      ]);
      if (runsRes.ok) { const d = await runsRes.json(); setRuns(d.runs || []); }
      if (workRes.ok) { const d = await workRes.json(); setWorkItems(d.items || []); }
      if (signalsRes.ok) { const d = await signalsRes.json(); setSignals(d.signals || []); }
      if (scenariosRes.ok) { const d = await scenariosRes.json(); setScenarios(d.scenarios || []); }
    } catch {}
    setLoading(false);
  };

  const handleSimulate = async (scenario) => {
    setSimulating(true);
    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/signals/simulate`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ scenario }) },
        orgId
      );
      if (res.ok) {
        const data = await res.json();
        if (data.error === 'insufficient_credits') {
          setBlockedModal({ type: 'credits', message: data.error_message || "You're out of credits" });
          toast.error(data.error_message || "You're out of credits");
        } else {
          toast.success('Signal processed');
          setSignals((prev) => [data, ...prev]);
          setSelectedSignal(data);
        }
        refreshUsage();
      } else { toast.error('Simulation failed'); }
    } catch { toast.error('Simulation failed'); }
    setSimulating(false);
  };

  const [approvalCount, setApprovalCount] = useState(0);
  const [batchActing, setBatchActing] = useState(false);

  const handleSignalAction = async (signalId, action, payload) => {
    setApprovingId(signalId);
    try {
      const body = { action, ...(payload || {}) };
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/signals/${signalId}/action`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
        orgId
      );
      if (res.ok) {
        const wasEdited = !!(payload && (payload.edited_reply || payload.edited_subject));
        toast.success(action === 'approve' ? (wasEdited ? 'Edited reply sent' : 'Sent') : 'Dismissed');
        setSignals((prev) => prev.map((s) =>
          s.signal_id === signalId
            ? {
                ...s,
                execution: { ...s.execution, status: action === 'approve' ? 'approved' : 'dismissed' },
                ...(wasEdited ? {
                  draft_reply: payload.edited_reply ?? s.draft_reply,
                  draft_reply_subject: payload.edited_subject ?? s.draft_reply_subject,
                } : {}),
              }
            : s
        ));
        if (selectedSignal?.signal_id === signalId) {
          setSelectedSignal((prev) => ({
            ...prev,
            execution: { ...prev.execution, status: action === 'approve' ? 'approved' : 'dismissed' },
            ...(wasEdited ? {
              draft_reply: payload.edited_reply ?? prev.draft_reply,
              draft_reply_subject: payload.edited_subject ?? prev.draft_reply_subject,
            } : {}),
          }));
        }
        setStartSignalEdit(false);
        if (action === 'approve') setApprovalCount((c) => c + 1);
      } else { toast.error('Action failed'); }
    } catch { toast.error('Action failed'); }
    setApprovingId(null);
  };

  const handleRequestSignalEdit = (signal) => {
    setSelectedSignal(signal);
    setStartSignalEdit(true);
  };

  const handleSelectSignal = (signal) => {
    setSelectedSignal(signal);
    setStartSignalEdit(false);
  };

  const handleBatchApprove = async () => {
    setBatchActing(true);
    const nonComplaintActionable = signals.filter(
      (s) => s.execution?.status === 'awaiting_approval' && s.classification?.category !== 'complaint'
    );
    for (const s of nonComplaintActionable) {
      try {
        await fetchWithOrg(
          `${BACKEND_URL}/api/signals/${s.signal_id}/action`,
          { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'approve' }) },
          orgId
        );
      } catch {}
    }
    toast.success(`${nonComplaintActionable.length} replies sent`);
    setSignals((prev) => prev.map((s) =>
      nonComplaintActionable.some((a) => a.signal_id === s.signal_id)
        ? { ...s, execution: { ...s.execution, status: 'approved' } }
        : s
    ));
    setBatchActing(false);
    setApprovalCount(0);
  };

  useEffect(() => { fetchData(); }, [orgId]);

  const handleWorkAction = async (itemId, action) => {
    setActing(true);
    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/service-os/work/${itemId}/action`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action }) },
        orgId
      );
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Action completed');
        await fetchData();
        setSelectedWork(null);
      } else { toast.error('Action failed'); }
    } catch { toast.error('Action failed'); }
    setActing(false);
  };

  /* Group work items by status */
  const pendingWork = workItems.filter((w) => w.status === 'pending_review' || w.status === 'needs_action');
  const completedWork = workItems.filter((w) => w.status !== 'pending_review' && w.status !== 'needs_action');
  const pendingCount = pendingWork.length;
  const actionableSignals = signals.filter((s) =>
    s.execution?.status === 'awaiting_approval' || s.execution?.status === 'escalated'
  );
  const autoHandledSignals = signals.filter((s) =>
    s.execution?.status === 'auto_handled'
  );
  const handledSignals = signals.filter((s) =>
    s.execution?.status !== 'awaiting_approval' && s.execution?.status !== 'escalated'
  );

  const pageTitle = isOperate ? 'Work' : 'Inbox';

  return (
    <>
      <SEO title={pageTitle} />
      {/* Blocked modal */}
      {blockedModal && (
        <BlockedModal
          type={blockedModal.type}
          message={blockedModal.message}
          onAction={() => { setShowTopUp(true); setBlockedModal(null); }}
          onClose={() => setBlockedModal(null)}
        />
      )}
      <TopUpModal
        isOpen={showTopUp}
        onClose={() => setShowTopUp(false)}
        defaultTab={blockedModal?.type === 'voice' ? 'voice' : 'credits'}
      />
      <div className="flex h-[calc(100vh-120px)] max-w-[1280px] mx-auto" data-testid="service-inbox">

        {/* ═══ LEFT PANEL ═══ */}
        <div className="w-[360px] flex-shrink-0 border-r border-[rgba(255,255,255,0.08)] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-[rgba(255,255,255,0.08)]">
            {/* Credit warning */}
            {creditStatus?.warning && (
              <div className="mb-3">
                <CreditWarningBanner
                  creditStatus={creditStatus}
                  onAddCredits={() => setShowTopUp(true)}
                />
              </div>
            )}
            <p className="text-xs text-[#7D8896] mb-3">
              {isOperate ? "How your business is running right now." : "How your workers are handling inbound signals."}
            </p>
            <div className="flex gap-1 bg-[#1F2937] rounded-lg p-0.5">
              <button
                onClick={() => setTab('signals')}
                data-testid="tab-signals"
                className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-md text-xs font-medium transition-colors ${
                  tab === 'signals'
                    ? 'bg-[#161C24] text-[#F0F4F8]'
                    : 'text-[#7D8896] hover:text-[#B0B8C4]'
                }`}
              >
                <Broadcast size={14} className="flex-shrink-0" />
                Signals
                {actionableSignals.length > 0 && (
                  <span className="min-w-[16px] h-4 flex-shrink-0 flex items-center justify-center rounded-full bg-[#F59E0B] text-[#12161C] text-[9px] font-bold px-1">
                    {actionableSignals.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setTab('work')}
                data-testid="tab-work"
                className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-md text-xs font-medium transition-colors ${
                  tab === 'work'
                    ? 'bg-[#161C24] text-[#F0F4F8]'
                    : 'text-[#7D8896] hover:text-[#B0B8C4]'
                }`}
              >
                <ListChecks size={14} className="flex-shrink-0" />
                Work
                {pendingCount > 0 && (
                  <span className="min-w-[16px] h-4 flex-shrink-0 flex items-center justify-center rounded-full bg-[#F59E0B] text-[#12161C] text-[9px] font-bold px-1">
                    {pendingCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setTab('runs')}
                data-testid="tab-runs"
                className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-md text-xs font-medium transition-colors ${
                  tab === 'runs'
                    ? 'bg-[#161C24] text-[#F0F4F8]'
                    : 'text-[#7D8896] hover:text-[#B0B8C4]'
                }`}
              >
                <Lightning size={14} className="flex-shrink-0" />
                Activity
              </button>
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <CircleNotch size={24} className="animate-spin text-[#00E5C0]" />
              </div>
            ) : tab === 'signals' ? (
              <>
                {/* Attention header */}
                <AttentionHeader awaitingCount={actionableSignals.length} autoCount={autoHandledSignals.length} totalCount={signals.length} />
                {/* Simulate panel */}
                {scenarios.length > 0 && (
                  <SimulatePanel scenarios={scenarios} onSimulate={handleSimulate} simulating={simulating} />
                )}
                {signals.length === 0 ? (
                  <div className="text-center py-16 px-4" data-testid="signals-empty-state">
                    <CheckCircle size={32} className="text-[#00E5C0] mx-auto mb-3" weight="light" />
                    <p className="text-base text-[#F0F4F8] font-medium">You're all caught up</p>
                    <p className="text-xs text-[#7D8896] mt-1">Your AI is handling everything</p>
                  </div>
                ) : actionableSignals.length === 0 ? (
                  <>
                    <div className="text-center py-10 px-4" data-testid="all-caught-up">
                      <CheckCircle size={28} className="text-[#00E5C0] mx-auto mb-2" weight="light" />
                      <p className="text-sm text-[#F0F4F8] font-medium">You're all caught up</p>
                      <p className="text-xs text-[#7D8896] mt-1">Your AI is handling everything</p>
                    </div>
                    {handledSignals.length > 0 && (
                      <div>
                        <div className="px-5 pt-2 pb-1">
                          <span className="text-[10px] text-[rgba(255,255,255,0.5)] uppercase tracking-[0.08em]">Recent activity</span>
                        </div>
                        {handledSignals.map((s) => (
                          <SignalRow key={s.signal_id} signal={s} isActive={selectedSignal?.signal_id === s.signal_id} onSelect={handleSelectSignal} onEdit={handleRequestSignalEdit} onApprove={handleSignalAction} approving={approvingId} />
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Contextual batch approve — shows after 2+ consecutive approvals */}
                    {approvalCount >= 2 && actionableSignals.filter(s => s.classification?.category !== 'complaint').length > 1 && (
                      <BatchApprovePrompt
                        count={actionableSignals.filter(s => s.classification?.category !== 'complaint').length}
                        onApproveAll={handleBatchApprove}
                        acting={batchActing}
                      />
                    )}
                    {actionableSignals.map((s) => (
                      <SignalRow key={s.signal_id} signal={s} isActive={selectedSignal?.signal_id === s.signal_id} onSelect={handleSelectSignal} onEdit={handleRequestSignalEdit} onApprove={handleSignalAction} approving={approvingId} />
                    ))}
                    {handledSignals.length > 0 && (
                      <div>
                        <div className="px-5 pt-3 pb-1">
                          <span className="text-[10px] text-[rgba(255,255,255,0.5)] uppercase tracking-[0.08em]">Recent activity</span>
                        </div>
                        {handledSignals.map((s) => (
                          <SignalRow key={s.signal_id} signal={s} isActive={selectedSignal?.signal_id === s.signal_id} onSelect={handleSelectSignal} onEdit={handleRequestSignalEdit} onApprove={handleSignalAction} approving={approvingId} />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </>
            ) : tab === 'work' ? (
              workItems.length === 0 ? (
                <div className="text-center py-12 px-4" data-testid="work-empty-state">
                  <CheckCircle size={28} className="text-[#00E5C0] mx-auto mb-2" weight="light" />
                  <p className="text-sm text-[#F0F4F8] font-medium">Nothing needs your attention</p>
                  <p className="text-xs text-[#7D8896] mt-1 mb-4">When your workers need a decision, it shows up here.</p>
                  <button
                    onClick={() => navigate('/automations/new')}
                    data-testid="work-empty-create"
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-[rgba(0,229,192,0.08)] text-[#00E5C0] text-xs font-medium rounded-lg border border-[rgba(0,229,192,0.12)] hover:bg-[rgba(0,229,192,0.14)] transition-colors"
                  >
                    <Plus size={12} weight="bold" />
                    Set up an AI worker
                  </button>
                </div>
              ) : (
                <>
                  {/* Pending section */}
                  {pendingWork.length > 0 && (
                    <div>
                      <div className="px-4 pt-3 pb-1">
                        <span className="text-[10px] font-semibold text-[#F59E0B] uppercase tracking-[0.08em]">Needs attention ({pendingWork.length})</span>
                      </div>
                      {pendingWork.map((w) => <WorkItem key={w.id} item={w} isActive={selectedWork?.id === w.id} onSelect={setSelectedWork} />)}
                    </div>
                  )}
                  {/* Completed section */}
                  {completedWork.length > 0 && (
                    <div>
                      <div className="px-4 pt-3 pb-1">
                        <span className="text-[10px] font-semibold text-[rgba(255,255,255,0.5)] uppercase tracking-[0.08em]">Completed recently ({completedWork.length})</span>
                      </div>
                      {completedWork.map((w) => <WorkItem key={w.id} item={w} isActive={selectedWork?.id === w.id} onSelect={setSelectedWork} />)}
                    </div>
                  )}
                </>
              )
            ) : (
              runs.length === 0 ? (
                <div className="text-center py-12 px-4" data-testid="activity-empty-state">
                  <Lightning size={28} className="text-[#7D8896] mx-auto mb-2" weight="light" />
                  <p className="text-sm text-[#F0F4F8] font-medium">No activity yet</p>
                  <p className="text-xs text-[#7D8896] mt-1">Activity appears when your workers complete tasks.</p>
                </div>
              ) : (
                runs.map((r) => <ActivityRow key={r.id} run={r} isActive={selectedRun?.id === r.id} onSelect={setSelectedRun} />)
              )
            )}
          </div>

          {/* Refresh */}
          <div className="p-3 border-t border-[rgba(255,255,255,0.08)]">
            <button
              onClick={fetchData}
              data-testid="refresh-inbox"
              className="w-full flex items-center justify-center gap-1.5 py-2 text-xs text-[#7D8896] hover:text-[#B0B8C4] transition-colors"
            >
              <ArrowClockwise size={12} />
              Refresh
            </button>
          </div>
        </div>

        {/* ═══ RIGHT PANEL ═══ */}
        <div className="flex-1 overflow-hidden">
          {tab === 'signals' ? (
            selectedSignal ? (
              <div className="h-full overflow-y-auto"><SignalDetail signal={selectedSignal} onAction={handleSignalAction} acting={!!approvingId} startInEdit={startSignalEdit} /></div>
            ) : (
              <EmptyRight icon={Broadcast} text="Your AI is handling your business in the background" />
            )
          ) : tab === 'runs' ? (
            selectedRun ? (
              <div className="h-full overflow-y-auto"><ActivityDetail run={selectedRun} /></div>
            ) : (
              <EmptyRight icon={Lightning} text={isOperate ? 'Select an activity to view details' : 'Select a run to view execution details'} />
            )
          ) : (
            selectedWork ? (
              <div className="h-full overflow-y-auto"><WorkDetail item={selectedWork} onAction={handleWorkAction} acting={acting} /></div>
            ) : (
              <EmptyRight icon={ListChecks} text="Select an item to review" />
            )
          )}
        </div>
      </div>
    </>
  );
}

const EmptyRight = ({ icon: Icon, text }) => (
  <div className="h-full flex items-center justify-center">
    <div className="text-center">
      <Icon size={32} className="text-[#7D8896] mx-auto mb-3" weight="light" />
      <p className="text-sm text-[#7D8896]">{text}</p>
    </div>
  </div>
);
